import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const UserDefineContext = createContext();

export const UserDefineProvider = ({ children, connectionString }) => {
    const { selectedCompany } = useAuth();

    const [userDefineData, setUserDefineData] = useState({});
    const [userDefineLoading, setUserDefineLoading] = useState(false);

    const fetchUserDefineData = useCallback(async (companyId) => {
        if (!companyId) return;
        setUserDefineLoading(true);
        try {
            const res = await axios.get(`${connectionString}/api/mastersalegrid`, {
                params: { companyId }
            });
            console.log('mastersalegrid:',res.data);
            setUserDefineData(res.data || {});
        } catch (err) {
            console.error('mastersalegrid fetch error:', err);
            setUserDefineData({});
        } finally {
            setUserDefineLoading(false);
        }
    }, [connectionString]);

    // AuthContext dispatches this ~2.8s after user + selectedCompany are ready
    // (see the 'userDefineTrigger' useEffect in AuthContext.js)
    useEffect(() => {
        const handler = (e) => {
            const companyId = e.detail?.companyId;
            if (companyId) fetchUserDefineData(companyId);
        };
        window.addEventListener('userDefineTrigger', handler);
        return () => window.removeEventListener('userDefineTrigger', handler);
    }, [fetchUserDefineData]);

    useEffect(() => {
        console.log('userDefineData:', userDefineData);
    }, [userDefineData]);

    // Additional Charges (userdefinecharges) are BookName-wise: each book (SALE, PURC, QUOT, SORDER, PORDER,
    // CHAL, PCHAL, PROFORMA, CREDIT, DEBIT, CREXP ...) can have its own Additional Charge 1-5.
    // They arrive as a flat array under userDefineData.additionalCharges (each row carries its BookName;
    // see MasterSaleGridController.getMasterSaleGrid).
    //   getAdditionalCharges(bookName) -> that book's own charge for a slot, else the GLOBAL charge (BookName
    //   empty) for that slot, so a book that has not configured a slot keeps using the existing global one.
    //   getAdditionalCharges()         -> the GLOBAL charges only, exactly as before.
    const getAdditionalCharges = useCallback((bookName) => {
        const all = userDefineData?.additionalCharges || [];
        if (!bookName) return all.filter(r => !r.BookName);
        const own = all.filter(r => r.BookName === bookName);
        const ownSlots = new Set(own.map(r => Number(r.SrNo)));
        const globalRows = all.filter(r => !r.BookName && !ownSlots.has(Number(r.SrNo)));
        return [...own, ...globalRows].sort((a, b) => Number(a.SrNo) - Number(b.SrNo));
    }, [userDefineData]);

    // Only the charge slots whose checkbox (ColToBeAsk) was selected when saved
    const getSelectedCharges = useCallback((bookName) => {
        return getAdditionalCharges(bookName).filter(r => Boolean(r.ColToBeAsk));
    }, [getAdditionalCharges]);

    // Get one charge slot's full row by its fixed position (SrNo, 1-5).
    // Accepts either (srNo) or (bookName, srNo).
    const getChargeRow = useCallback((a, b) => {
        const srNo = b !== undefined ? b : a;
        const bookName = b !== undefined ? a : undefined;
        return getAdditionalCharges(bookName).find(r => Number(r.SrNo) === Number(srNo));
    }, [getAdditionalCharges]);

    // The charge's linked productmaster row (the charge-item created for this slot's TransType — per book for
    // BookName-wise charges, see createChargeItem / UserDefineChargesController) — already present as ItemId
    // on every row from getAdditionalCharges/getChargeRow, this is just a convenience accessor.
    // Accepts either (srNo) or (bookName, srNo).
    const getChargeItemId = useCallback((a, b) => {
        return getChargeRow(a, b)?.ItemId ?? null;
    }, [getChargeRow]);

    // Memoize refetch callback separately to maintain stable reference
    const refetchUserDefineData = useCallback(() => {
        fetchUserDefineData(selectedCompany?.CompId);
    }, [fetchUserDefineData, selectedCompany?.CompId]);

    // Memoize context value to prevent unnecessary re-renders of entire app tree
    const contextValue = useMemo(() => ({
        userDefineData,
        userDefineLoading,
        refetchUserDefineData,
        getAdditionalCharges,
        getSelectedCharges,
        getChargeRow,
        getChargeItemId,
    }), [
        userDefineData,
        userDefineLoading,
        refetchUserDefineData,
        getAdditionalCharges,
        getSelectedCharges,
        getChargeRow,
        getChargeItemId,
    ]);

    return (
        <UserDefineContext.Provider value={contextValue}>
            {children}
        </UserDefineContext.Provider>
    );
};

export const useUserDefine = () => useContext(UserDefineContext);
